<template>
  <div
    class="access-code--wrapper"
    :class="{
      'pointer-events-none': isDisableEdit
    }"
  >
    <span @click.stop="showAccessCodeInput" class="access-code--text relative" v-if="!isShowAccessCodeEditInput">
      <span>{{ presentation.accessCode }}</span>
      <font-awesome-icon
        icon="pencil-alt"
        class="invisible ml-1 access-code--edit-icon md:inline"
      />
    </span>

    <div
      v-else
      :id="`access-code--editor-${presentation.accessCode}`"
      class="flex flex-col"
    >
      <div class="flex flex-row items-center">
        <aha-input
          size="sm"
          v-model="newAccessCode"
          :ref="`access-code-${presentation.accessCode}`"
          :id="`access-code-${presentation.accessCode}`"
          class="access-code--input"
          classes="uppercase"
          :placeholder="presentation.accessCode"
          @keyup.enter="changeAccessCode()"
          :maxLength="10"
        />

        <aha-spinner variant="dark" v-if="isSubmittingChangeAccessCode || isCheckingPerm" />
        <aha-icon
          v-if="!isSubmittingChangeAccessCode && !isCheckingPerm"
          name="check"
          width="24"
          height="24"
          class="access-code--save-icon"
          @click="changeAccessCode()"
        />
      </div>
      <div class="mt-1" v-if="textError">
        <span class="text-red-100">{{ textError }}</span>
      </div>
    </div>
    <br>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import vClickOutside from 'v-click-outside'
import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'

import Utils from '@/util'
import Socket from '@/socket'

import constants from '@/constant'
import EventBus from '@/eventBus'

const { bind, unbind } = vClickOutside.directive

const { eventCategories } = constants

export default {
  props: {
    presentation: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      ctaUpgrade: false,
      newAccessCode: '',
      textError: '',
      isShowAccessCodeEditInput: false,
      isSubmittingChangeAccessCode: false,
      isCheckingPerm: false,
    }
  },
  computed: {
    ...mapState({
      userId: (state) => state.currentUser && state.currentUser.id,
      currentUser: (state) => state.currentUser,
    }),
    isBeforeCurrentPeriodStart() {
      return this.$store.state.currentUser.isBeforeCurrentPeriodStart
    },
    isShowTooltip() {
      return this.isBeforeCurrentPeriodStart ? this.isProPlan : !this.isFreePlan
    },
    isProPlan() {
      return this.$store.state.plan.group === 'pro'
    },
    isWhiteLabel() {
      return this.$store.state.plan.group === 'white-label'
    },
    isDisableEdit() {
      if (this.currentUser.accountRole
        && this.currentUser.accountRole === constants.workspace.VIEWER
        && this.$store.state.workingMode === constants.workspace.TEAM) return true
      return this.presentation &&
        this.presentation.userId !== this.userId &&
        this.presentation.takenOverBy !== this.userId
    },
  },
  mounted() {
    this.newAccessCode = this.presentation.accessCode
  },
  methods: {
    clickOutside() {
      this.ctaUpgrade = false
    },
    showAccessCodeInput() {
      if (!(this.isWhiteLabel || this.isProPlan)) {
        EventBus.$emit(`open-share-edit-modal-${this.presentation.id}`)
        return
      }
      this.isShowAccessCodeEditInput = true
      this.$nextTick(() => {
        this.editor = document.getElementById(`access-code--editor-${this.presentation.accessCode}`)
        bind(this.editor, { value: this.onBlurAccessCodeInput })
        const inputWrpElem = document.getElementById(`access-code-${this.presentation.accessCode}`)
        if (inputWrpElem) {
          const inputElem = inputWrpElem.getElementsByTagName('input')[0]
          if (inputElem) {
            inputElem.select()
          }
        }
      }) // Wait for next event-loop tick to focus the mounted element
    },

    onBlurAccessCodeInput() {
      this.hideAccessCodeInput()
      if (!this.isSubmittingChangeAccessCode) {
        this.newAccessCode = this.presentation.accessCode
      }
    },

    hideAccessCodeInput() {
      if (this.isSubmittingChangeAccessCode) return
      this.isShowAccessCodeEditInput = false
      this.unbindClickOutside(this.editor)
    },

    unbindClickOutside() {
      unbind(this.editor)
      if (this.textError) {
        this.newAccessCode = this.presentation.accessCode
      }
      this.textError = ''
    },

    async changeAccessCode() {
      if (!this.newAccessCode || !this.newAccessCode.trim()) {
        this.isSubmittingChangeAccessCode = false
        this.isShowAccessCodeEditInput = false
        this.unbindClickOutside(this.editor)
        return
      }
      this.textError = ''

      if (this.newAccessCode === this.presentation.accessCode) {
        this.isShowAccessCodeEditInput = false
        this.unbindClickOutside(this.editor)
        return
      }

      // calling perm check will trigger clickOutside
      // so unbind it first and bind again later
      this.unbindClickOutside(this.editor)
      this.isCheckingPerm = true
      const hasPerm = await this.$func.isCurrentEditorRealtime(this.presentation, this)
      this.isCheckingPerm = false
      bind(this.editor, { value: this.onBlurAccessCodeInput })

      if (!hasPerm) return
      this.isSubmittingChangeAccessCode = true
      try {
        await PresentationApi.changeAccessCode(this.presentation.id, {
          socketId: Socket.getSocketId(),
          accessCode: this.newAccessCode,
        })

        this.$emit('updateAccessCode', {
          id: this.presentation.id,
          newAccessCode: this.newAccessCode,
        })

        this.isShowAccessCodeEditInput = false

        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_LIST,
          'Presentation > Change access code > Succeeded',
          `New access code: ${this.newAccessCode}`,
        )
        this.unbindClickOutside(this.editor)
      } catch (error) {
        Sentry.captureException(error)
        if (error.body && error.body.message) {
          this.textError = error.body.message
        } else {
          Utils.showToastError(this.$i18nPresenter.t('Changing access code failed. Please try again.'))
        }

        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_LIST,
          'Presentation > Change access code > Failed',
          `New access code: ${this.newAccessCode}`,
        )
        throw error
      } finally {
        this.isSubmittingChangeAccessCode = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.access-code--wrapper {
  @apply flex flex-row items-center;
  width: 6.25rem;
}
.access-code--text {
    @apply transition-colors;
    .access-code--edit-icon {
        @apply invisible;
    }
    &:hover {
        @apply cursor-pointer text-primary-90;
        .access-code--edit-icon {
            @apply visible text-primary-90;
        }
    }
}

.access-code--input {
    @apply mr-1;
    width: 5.5rem;
}
.access-code--save-icon {
    @apply cursor-pointer text-primary-90;
}

.upgrade-arrow {
  width: 0;
  height: 0;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent rgba(40, 46, 62, var(--tw-bg-opacity)) transparent;
  border-style: solid;
  position: absolute;
  z-index: 1;
  top: 1.6rem;

  &.upgrade-arrow_slide {
    left: 40.4%;
  }
}
</style>
